<script setup lang="ts" generic="T">
import type { IIndexHeader, IPaginationNav, IPaginationApi, IHeaderNav } from "~/utils/interfaces/table-interfaces";
import type { TrainingPlanIndividual } from "~/utils/interfaces/sessions-interfaces";
import Pagination from "~/components/Indexs/Pagination.vue";
import CheckboxInput from "~/components/Common/Inputs/CheckboxInput.vue";
import Kpi from "~/components/Common/Kpi.vue";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import { isGridRowChecked, isSameGridRow } from "~/utils/helpers/index-grid";
import type { PropType } from "vue";
import { useSettingsStore } from "~/store/settings/settings.store";
import { useAuthStore } from "~/store/auth/auth.store";
import PQueue from "p-queue";
import { debounce } from "perfect-debounce";
import { ItemType } from "@simbelapp/order-sdk";
import { DebounceEnum } from "~/utils/enums/debounce.enums";
import { sanitizeHtml } from "~/utils/helpers/sanitize";
import { SettingScopesEnum } from "@simbelapp/order-sdk";
import Tooltip from "~/components/Common/Tooltip.vue";
import TruncatedText from "~/components/Common/TruncatedText.vue";

const projectStore = useCreateProjectStore();
const settingsStore = useSettingsStore();
const { gridDetails } = storeToRefs(settingsStore);
const authStore = useAuthStore();
const props = defineProps({
  headers: {
    type: Array as PropType<IIndexHeader[]>,
  },
  rows: {
    type: [Object, Array] as PropType<IPaginationApi<T> | T[]>,
  },
  selectedNav: { type: Object as PropType<IHeaderNav | null>, required: false },
  itemPerPage: { type: Number },
  showKpi: { type: Boolean },
  checkbox: { type: Boolean },
  checkBoxReadOnly: { type: Boolean, default: false },
  checkboxItemsDisabled: { type: Array as PropType<Record<string, any>>, required: false, default: [] },
  selectAll: { type: Boolean },
  noPagination: { type: Boolean },
  url: { type: String, required: false, default: "" },
  itemKeys: { type: Array as PropType<string[]>, required: false, default: [] },
  store: {
    type: Object,
    required: false,
  },
  trackBy: {
    type: String,
    required: false,
    default: "id",
  },
  empty: {
    type: Boolean,
    required: false,
    default: false,
  },
  scope: {
    type: String,
    required: false,
  },
  resize: {
    type: Boolean,
    required: false,
    default: false,
  },
  hover: {
    type: Boolean,
    default: true,
  },
  equalColumns: {
    type: Boolean,
    default: false,
  },
  paginationKey: {
    type: String,
    required: false,
  },
  pageContainer: {
    type: Boolean,
    default: true,
  },
  nav: {
    type: Boolean,
    default: false,
  },
  paginationDisabled: {
    type: Boolean,
    default: false,
  },
  bordered: {
    type: Boolean,
    default: false,
  },
});

const rowsNormalized = computed<IPaginationApi<T>>(() => {
  if (Array.isArray(props.rows)) {
    return {
      meta: {
        count: props.rows.length,
        totalCount: null,
      },
      data: props.rows,
    };
  }

  return (
    (props.rows as IPaginationApi<T> | undefined) ?? {
      meta: {
        count: 0,
        totalCount: null,
      },
      data: [],
    }
  );
});

const selectAllPageItems = ref<boolean>(false);
const headerClass = ref<string>("grid__headers_main");
const headerItemClass = ref<string>("grid_header_item_main");

const selectedItems = ref<Record<string, any>[]>([]);
const excludedItems = ref<Record<string, any>[]>([]);
const lastValueChanged = ref<number | undefined | null>(
  props?.selectedNav?.nb ?? rowsNormalized.value?.meta?.count ?? 0,
);

const emit = defineEmits(["clickRow", "pageNavigate", "sort", "showMore"]);
defineExpose({ selectedItems, excludedItems });
const containerRef = ref<HTMLElement | null>(null);

// Matches the inline padding of `.index_row`, the wrapper of every cell's content.
const CELL_INLINE_PADDING_PX = 8;
const columnWidthOwner = Symbol("IndexGrid");
let columnObserver: ResizeObserver | null = null;
let measureFrameId: number | null = null;

function measureColumns() {
  measureFrameId = null;
  const cells = containerRef.value?.querySelectorAll<HTMLElement>("thead th[data-col-key]");
  if (!cells?.length) return;
  const widths: Record<string, number> = {};
  cells.forEach((cell) => {
    // A hidden grid (v-show, inactive tab) reads 0 and must not override a visible grid's width.
    if (cell.clientWidth > 0) widths[cell.dataset.colKey as string] = cell.clientWidth - CELL_INLINE_PADDING_PX;
  });
  settingsStore.setMeasuredWidths(widths, columnWidthOwner);
}

function scheduleMeasureColumns() {
  if (measureFrameId === null) measureFrameId = requestAnimationFrame(measureColumns);
}

function observeColumns() {
  if (typeof ResizeObserver === "undefined") return;
  columnObserver ??= new ResizeObserver(scheduleMeasureColumns);
  columnObserver.disconnect();
  containerRef.value
    ?.querySelectorAll<HTMLElement>("thead th[data-col-key]")
    .forEach((cell) => columnObserver?.observe(cell));
}

function stopMeasuringColumns() {
  columnObserver?.disconnect();
  columnObserver = null;
  if (measureFrameId !== null) cancelAnimationFrame(measureFrameId);
  measureFrameId = null;
  settingsStore.clearMeasuredWidths(columnWidthOwner);
}

onMounted(() => {
  if (containerRef.value) {
    containerRef.value.addEventListener("scroll", handleScroll);
  }
});

onBeforeUnmount(() => {
  if (containerRef.value) {
    containerRef.value.removeEventListener("scroll", handleScroll);
  }
  stopMeasuringColumns();
});
const visibleHeaders = computed(() =>
  props.scope
    ? gridDetails.value.headersValues?.filter((h: any) => h.visible)
    : props.headers?.filter((h) => h.visible),
);

onMounted(observeColumns);
watch(() => settingsStore.measuredWidthsRevision, scheduleMeasureColumns);
watch(() => [visibleHeaders.value?.map((h: IIndexHeader) => h.key).join("|"), props.empty], observeColumns, {
  flush: "post",
});

function clickRow(val: any) {
  emit("clickRow", val);
}

function navigate(val: IPaginationNav) {
  emit("pageNavigate", val);
}

async function sort(val: IIndexHeader) {
  if (val.key && props.scope) {
    if (gridDetails.value.sortingValues.length > 0) {
      gridDetails.value.sortingValues[0].sortingColumn = val.key;
      gridDetails.value.sortingValues[0].order = gridDetails.value.sortingValues[0].order === "ASC" ? "DESC" : "ASC";
    } else {
      gridDetails.value.sortingValues.push({
        sortingColumn: val.key,
        scope: val.scope as SettingScopesEnum,
        order: "ASC",
      });
    }
  } else {
    val.sortOrder = val.sortOrder === "ASC" ? "DESC" : "ASC";
  }
  emit("sort", val);
}

watch(
  () => props?.selectedNav?.nb,
  () => {
    lastValueChanged.value = props?.selectedNav?.nb;
  },
);

watch(
  () => rowsNormalized.value?.meta?.count,
  () => {
    if (!props?.selectedNav) {
      lastValueChanged.value = rowsNormalized.value?.meta?.count;
    }
  },
);

watch(
  () => props.selectAll,
  () => {
    selectedItems.value = [];
    excludedItems.value = [];
    selectAllPageItems.value = props.selectAll;
  },
);

watch(
  () => selectedItems?.value,
  () => {
    if (!selectedItems?.value?.length && !props.selectAll) {
      selectAllPageItems.value = false;
    }
  },
);

watch(
  () => rowsNormalized.value?.data,
  () => {
    if (rowsNormalized.value.data?.length) {
      checkSelectPage();
    }
  },
);

async function selectPage(selectPage: boolean) {
  if (selectPage) {
    updateSelectedItems(selectPage, rowsNormalized.value.data);
  } else {
    rowsNormalized.value.data?.forEach((d) => updateSelectedItems(selectPage, d));
  }
}

function isSameRow(a: any, b: any): boolean {
  return isSameGridRow(a, b, props.trackBy);
}

function isRowChecked(row: any): boolean {
  return isGridRowChecked(
    row,
    { selectAll: props.selectAll, selectedItems: selectedItems.value, excludedItems: excludedItems.value },
    props.trackBy,
  );
}

function updateSelectedItems(selected: boolean, item: any) {
  if (props.selectAll) {
    if (selected) {
      const rawItem = toRaw(item);
      const itemsArray = Array.isArray(rawItem) ? rawItem : [rawItem];
      excludedItems.value = excludedItems.value.filter((el) => {
        return !itemsArray.some((i: any) => isSameRow(el, i));
      });
    } else {
      excludedItems.value.push(item);
    }
  } else if (selected) {
    // `selectPage(true)` hands us the whole page as an array to add in one go;
    // row clicks hand us a single row. Spread arrays, push single rows.
    // (An eslint auto-fix once added `.value` to `selectAllPageItems` here, which
    // silently disabled the spread on first click — select-all-on-page then stored
    // the array as one item, showing "1/N" with no rows checked.)
    if (Array.isArray(item)) {
      selectedItems.value = [...selectedItems.value, ...item];
    } else {
      selectedItems.value.push(item);
    }
  } else {
    selectedItems.value = selectedItems.value.filter((el) => !isSameRow(el, item));
  }

  selectedItems.value = [...new Set(selectedItems.value)];
  excludedItems.value = [...new Set(excludedItems.value)];

  // Don't set usersId when "select all" is active - let the watcher handle fetching ALL users
  if (selectedItems.value && !props.selectAll) {
    // The grid is generic, so its rows are untyped here; the grids feeding the project funnel carry attendee fields.
    projectStore.project.users.usersId = selectedItems.value as TrainingPlanIndividual.ISessionDetailsAttendees[];
  }

  checkSelectPage();
}

function checkSelectPage() {
  if (!props.selectAll && rowsNormalized.value.data?.every((el) => selectedItems.value.some((v) => isSameRow(el, v)))) {
    selectAllPageItems.value = true;
  } else if (
    props.selectAll &&
    !excludedItems.value.some((el) => rowsNormalized.value.data?.some((d) => isSameRow(d, el)))
  ) {
    selectAllPageItems.value = props.selectAll;
  } else {
    selectAllPageItems.value = false;
  }
}

const gridColumnsValues = computed(() => {
  return visibleHeaders.value?.map((h: IIndexHeader) => {
    return `minmax(${h.minWidthPx}px, ${h.maxWidthFr}fr)`;
  });
});

const gridColumnsValuesResize = computed(() => {
  return visibleHeaders.value
    ?.map((h: IIndexHeader) => {
      return `${h.minWidthPx}px`;
    })
    .join(" ");
});

const gridRowsTemplate = computed(() => {
  if (props.resize) {
    if (props.checkbox) {
      return {
        "grid-template-columns": `16px ${gridColumnsValuesResize.value}`,
      };
    } else {
      return {
        "grid-template-columns": gridColumnsValuesResize.value,
      };
    }
  } else {
    if (props.checkbox) {
      return {
        "grid-template-columns": `minmax(${16}px, ${0.5}fr) ${gridColumnsValues.value?.join(" ")}`,
      };
    } else {
      return { "grid-template-columns": gridColumnsValues.value?.join(" ") };
    }
  }
});

const rightClickURL = (values: Record<string, any>, keys: string[]) => {
  const insert = (str: string, index: number, value: string) => {
    return str.substring(0, index) + value + str.substring(index);
  };

  if (keys?.length) {
    let url = props.url;

    keys.forEach((key) => {
      const i = url.indexOf("__KEY__");
      if (i != -1 && i != undefined) {
        url = url.substring(0, i) + url.substring(i + "__KEY__".length);
        url = insert(url, i, values[key]);
      }
    });
    return url;
  }
  return undefined;
};

function isCheckboxDisabled(item: any) {
  if (props.checkBoxReadOnly) {
    return true;
  }

  return props.checkboxItemsDisabled?.some((check: any) => isSameRow(check, item));
}

const resizingColumn = ref<number | null>(null);
const startX = ref<number>(0);
const startWidth = ref<number>(0);
const blueLine = ref<boolean>(false);
const blueLinePosition = ref<number>(0);
const deltaX = ref<number>(0);
const deltaX2 = ref<number>(0);
let animationFrameId: number | null = null;

function initResize(event: MouseEvent, index: number) {
  event.preventDefault();
  const element = (event.target as HTMLElement).closest(".grid__container");

  if (element) {
    const tableOffsetLeft = element.getBoundingClientRect().left;
    const scrollLeft = element.scrollLeft; // Get the scroll position
    blueLinePosition.value = event.pageX - tableOffsetLeft + scrollLeft; // Adjust by scroll position
  }

  blueLine.value = true;
  resizingColumn.value = index;
  startX.value = event.pageX;
  startWidth.value = (visibleHeaders.value as IIndexHeader[])[index]?.minWidthPx ?? 0;

  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", stopResize);
}

const onMouseMove = (event: MouseEvent) => {
  if (resizingColumn.value !== null) {
    // Annuler une animation en cours si elle n'a pas encore été exécutée
    if (animationFrameId === null) {
      animationFrameId = requestAnimationFrame(() => {
        const container = (event.target as HTMLElement).closest(".grid__container");
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const scrollLeft = container.scrollLeft;

        // Calcul du delta entre la position actuelle et la position de départ
        deltaX.value = event.pageX - containerRect.left - startX.value;
        deltaX2.value = event.pageX - startX.value;

        // Mettre à jour la position de la ligne bleue pour qu'elle suive la souris
        blueLinePosition.value = event.pageX - containerRect.left + scrollLeft;

        // Réinitialiser `animationFrameId` après l'exécution
        animationFrameId = null;
      });
    }
  }
};

function stopResize() {
  // Supprimer les événements et réinitialiser le style du curseur
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", stopResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";

  // Calculer la nouvelle largeur de la colonne redimensionnée
  const newWidth = Math.max(startWidth.value + deltaX2.value, 50);

  if (resizingColumn.value !== null) {
    (visibleHeaders.value as IIndexHeader[])[resizingColumn.value].minWidthPx = newWidth;
  }

  resizingColumn.value = null;
  blueLine.value = false;

  // Annuler une frame d'animation si nécessaire
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
  if (props.scope) updateColumnWidth();
  deltaX.value = 0;
  deltaX2.value = 0;
}

const queue = new PQueue({ concurrency: 1 });

const updateColumnWidth = debounce(
  () => {
    queue.clear();

    queue.add(async () => {
      if (authStore.user?.userId && props.scope) {
        await settingsStore.updateGridSettings(authStore.user?.userId, ItemType.User);
      }
    });
  },
  DebounceEnum.DEFAULT,
  { trailing: false },
);

const showKpi = ref(true);

function handleScroll(event: Event) {
  const target = event.target as HTMLElement;
  if (target.scrollTop > 0) {
    showKpi.value = false;
  } else {
    showKpi.value = true;
  }
}
</script>
<template>
  <Transition>
    <Kpi v-if="props?.showKpi && showKpi" :store="store"> </Kpi>
  </Transition>
  <!-- No border while empty: the #empty content (an EmptyState) brings its own. -->
  <div
    ref="containerRef"
    class="grid__container"
    :class="{ 'grid__container--bordered': props.bordered && !props.empty }"
  >
    <div v-if="blueLine" class="blue-resize-line" :style="{ left: blueLinePosition + 'px' }"></div>
    <div
      v-if="blueLine"
      class="blue-resize-line"
      :style="{ left: blueLinePosition + 'px', height: '42px', width: '4px', transform: 'translateX(-1px)' }"
    ></div>

    <table
      v-if="!empty"
      class="grid__table"
      :style="{ ...gridRowsTemplate, '--checkbox-width': props.pageContainer ? '50px' : '30px' }"
      :class="{ 'header-fixed': props.resize || props.equalColumns, 'with-checkbox': !props.checkbox }"
    >
      <thead class="grid__thead">
        <tr class="grid__tr w-full">
          <th
            v-if="props.checkbox"
            :class="`${headerClass} p-0 ${props.pageContainer ? 'w-[50px]' : 'w-[30px]'} ${!showKpi && props.nav && props.showKpi ? 'no-border-top' : 'border-top'}`"
          >
            <div class="flex justify-center">
              <CheckboxInput
                :checked="selectAllPageItems"
                :value="selectAllPageItems"
                :disabled="props.checkBoxReadOnly"
                @update:model-value="selectPage($event)"
              ></CheckboxInput>
            </div>
          </th>
          <th
            v-for="(header, index) in visibleHeaders"
            :key="`key-${header.name}`"
            :data-col-key="header.key"
            :class="`${headerClass} ${!showKpi && props.nav && props.showKpi ? 'no-border-top' : 'border-top'}`"
            :style="
              props.resize || !props.equalColumns
                ? { width: header.minWidthPx + 'px' }
                : { minWidth: header.minWidthPx + 'px' }
            "
          >
            <div
              class="flex items-center select-none"
              :class="{
                'justify-start': !header.align || header.align === 'left',
                'justify-center': header.align === 'center',
                'justify-end': header.align === 'right',
                'justify-between': header.sort && (!header.align || header.align === 'left'),
              }"
            >
              <div
                class="flex items-center"
                :class="{
                  'flex-row-reverse': header.align === 'right' && header.sort,
                }"
              >
                <TruncatedText
                  v-if="header.name"
                  :text="header.name"
                  :text-class="headerItemClass"
                  :align="header.align"
                />
                <Tooltip v-if="header.tooltip" :position="'top-mid'" append-to-body>
                  <template #content>
                    <div class="flex w-[420px] gap-[8px]">
                      <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                      <div class="flex flex-col gap-2 body_regular" v-html="sanitizeHtml(header.tooltip)"></div>
                    </div>
                  </template>
                </Tooltip>
              </div>
              <img
                v-if="header.sort"
                class="grid__sort_icon"
                src="/icons/generic/sort_column.svg"
                alt=""
                @click="sort(header)"
              />
            </div>
            <div v-if="props.resize" class="resizer" @mousedown.stop="initResize($event, index)"></div>
          </th>
        </tr>
      </thead>
      <tbody v-if="!!rowsNormalized?.data" class="grid__tbody">
        <tr
          v-for="(values, k) in rowsNormalized?.data as any"
          :key="`item-${k}`"
          class="grid__tr grib__body_tr"
          :class="{ hover: hover }"
          @click.prevent="clickRow(values)"
        >
          <td v-if="props.checkbox" class="w-[50px] min-w-[50px] max-w-[50px]" @click.stop>
            <div class="flex justify-center">
              <CheckboxInput
                id="checkbox"
                class="index_row"
                :checked="isRowChecked(values)"
                :value="isRowChecked(values)"
                :disabled="isCheckboxDisabled(values)"
                @update:model-value="updateSelectedItems($event, values)"
              >
              </CheckboxInput>
            </div>
          </td>
          <slot
            style="display: contents"
            :href="rightClickURL(values as any, props.itemKeys)"
            name="row"
            :row="values"
            @click.prevent.self
          ></slot>
        </tr>
      </tbody>
    </table>
    <slot v-else name="empty">empty</slot>
  </div>
  <Pagination
    v-if="!noPagination && !empty"
    :class="['pt-3 px-4 border-t border-[var(--grey-lightest)]', { pagination_disabled: props.paginationDisabled }]"
    :disabled="props.paginationDisabled"
    disabled-tooltip-message="Pagination indisponible en mode édition"
    :nb-items="lastValueChanged ?? 0"
    :item-per-page="itemPerPage as number"
    :pagination-key="props.paginationKey"
    @navigate="navigate"
  />
</template>

<style lang="scss">
@use "/assets/scss/texts.scss";

.grid__container {
  position: relative;
  @apply overflow-x-auto flex-1;
}

/* Opt-in border for standalone IndexGrid usages (mirrors IndexLayout's `bordered`). */
.grid__container--bordered {
  /* overflow-auto (not -hidden): clip rounded corners but allow horizontal scroll. */
  @apply border border-stroke-color rounded-lg overflow-auto;
}

.grid__container--bordered thead th {
  @apply border-t-0;
}

.grid__container--bordered tbody tr:last-child td {
  @apply border-b-0;
}

/* drop the pagination's top border so it doesn't double the bordered box's bottom edge */
.grid__container.grid__container--bordered + * {
  @apply border-t-0;
}

.grid__headers_main {
  background: var(--grey-light);
  border-bottom: 1px solid var(--grey-lightest);
  z-index: 101;
}

.no-border-top {
  border-top: none;
}

.border-top {
  border-top: 1px solid var(--grey-lightest);
}

.grid__headers_secondary {
  @apply border-b-[1px];
  border-color: var(--grey-lightest);
  background: var(--white-main);
  z-index: 101;
}

.grid__sort_icon {
  @apply cursor-pointer h-[25px] w-[16px];
}

.header-fixed {
  table-layout: fixed;
}

.grid__table {
  width: 100%;
  table-layout: fixed;

  td {
    padding: 4px 0;
  }
}

table {
  border-collapse: separate;
  border-spacing: 0;
}

.grid__thead,
.grid__tbody,
.grid__tr {
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  overflow: hidden !important;

  div {
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    overflow: hidden !important;
  }
}

.grib__body_tr {
  border-radius: 10px;

  td {
    border-bottom: 1px solid var(--grey-lightest);

    &:first-child {
      border-radius: 4px 0 0 4px;
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }
  }
}

.hover {
  &:hover {
    @apply cursor-pointer;

    td {
      background: var(--grey-light);
    }
  }
}

.grid_header_item_main {
  @apply px-[4px] py-[12px] text-start meta;
  color: var(--text-tertiary-color);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.grid_header_item_secondary {
  @apply px-[8px] py-[12px] text-start text-[14px] body_regular;
  color: var(--grey-medium);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.grid_header_item_secondary::first-letter {
  text-transform: capitalize;
}

.resizer {
  width: 4px;
  cursor: col-resize;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
}

.resizer:hover {
  background: var(--primary-blue-color);
}

th,
td {
  position: relative;
}

.blue-resize-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: var(--primary-blue-color);
  z-index: 1000;
}

.header-fixed thead th {
  position: sticky;
  top: 0;
}

.header-fixed thead th:first-child,
.header-fixed tbody td:first-child {
  position: sticky;
  left: 0;
  z-index: 102;
}

.header-fixed:not(.with-checkbox) thead th:first-child,
.header-fixed:not(.with-checkbox) thead th:nth-child(2),
.header-fixed thead th:first-child {
  z-index: 103;
}

.header-fixed:not(.with-checkbox) tbody td:nth-child(2) {
  z-index: 102;
}

.header-fixed:not(.with-checkbox) thead th:nth-child(2),
.header-fixed:not(.with-checkbox) tbody td:nth-child(2) {
  position: sticky;
  left: var(--checkbox-width, 50px);
}

.header-fixed thead td:first-child,
.header-fixed:not(.with-checkbox) tbody td:nth-child(2),
.header-fixed tbody td:first-child,
.header-fixed tbody td:nth-child(2) {
  background: var(--white-main);
}

.with-checkbox thead th:first-child,
.with-checkbox tbody td:first-child {
  padding-left: 12px;
}

.header-fixed tbody tr.hover:hover td:first-child,
.header-fixed tbody tr.hover:hover td:nth-child(2) {
  background: var(--grey-light);
}

.pagination_disabled {
  @apply opacity-70 cursor-not-allowed;
}
</style>
<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.1s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
